import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  Ban,
  BarChart3,
  Check,
  ChevronRight,
  FileDown,
  Globe,
  Lock,
  RotateCcw,
  Search,
  Server,
  Swords,
  Users,
  Zap,
} from "lucide-react";
import { BackgroundGlow } from "~/components/background-glow";
import { FadeIn } from "~/components/fade-in";
import { SignedIn } from "~/components/signed-in";
import { SignedOut } from "~/components/signed-out";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

// ─── Data ──────────────────────────────────────────────────────────────────

const basicFeatures = [
  { icon: Ban, label: "Ban / Unban players across all servers" },
  { icon: Users, label: "Kick & tribe-ban support" },
  { icon: Search, label: "Player, gamertag & tribe search" },
  { icon: Activity, label: "Join/Leave, chat, kill & tribe logs" },
  { icon: Server, label: "Server management — restart, stop, status" },
  { icon: BarChart3, label: "Live population tracking channel" },
  { icon: Globe, label: "Platform detection (PC vs Xbox)" },
  { icon: Lock, label: "Admin command monitoring + autoban" },
];

const advancedFeatures = [
  { icon: Swords, label: "Season overview & statistics" },
  { icon: AlertTriangle, label: "Spoof detection with optional autoban" },
  { icon: Users, label: "Alt & dupe account detection" },
  { icon: BarChart3, label: "K/D & playtime leaderboards" },
  { icon: FileDown, label: "File upload / download from server" },
  { icon: RotateCcw, label: "Rollback from backups" },
  { icon: Zap, label: "Wipe management & settings presets" },
  { icon: Lock, label: "Gamerscore change monitoring" },
];

const plans = [
  {
    name: "Basic",
    price: "$12.99",
    period: "/mo",
    description: "Everything you need to run a well-managed cluster.",
    badge: null,
    features: [
      "All Basic commands",
      "Ban / Unban / Kick / Tribe-ban",
      "Player & tribe search",
      "Chat, kill, admin & tribe logs",
      "Join / Leave & status logs",
      "Server management controls",
      "Live population channel",
    ],
    cta: "Get Started",
    ctaVariant: "outline" as const,
  },
  {
    name: "Advanced",
    price: "$20.99",
    period: "/mo",
    description: "Advanced anti-cheat, stats & full server control.",
    badge: "Most Popular",
    features: [
      "Everything in Basic",
      "Spoof & alt detection",
      "K/D & playtime leaderboards",
      "Season statistics overview",
      "File management & rollback",
      "Wipe management",
      "Gamerscore monitoring",
    ],
    cta: "Get Advanced",
    ctaVariant: "default" as const,
  },
];

// ─── Sections ──────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative flex min-h-screen flex-col items-center justify-center px-6 pt-16">
      <div className="relative z-10 mx-auto max-w-4xl text-center mt-10">
        <FadeIn immediate delay={0}>
          <h1 className="mb-6 text-5xl font-extrabold leading-tight tracking-tight text-foreground md:text-7xl">
            Total control over
            <br />
            <span className="bg-linear-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">
              your ARK cluster
            </span>
          </h1>
        </FadeIn>

        <FadeIn immediate delay={90}>
          <p className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground md:text-xl">
            Overseer connects your Discord to your Nitrado servers — ban
            players, monitor logs, detect cheaters, and manage every aspect of
            your cluster, all from one place.
          </p>
        </FadeIn>

        <FadeIn immediate delay={180}>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button size="lg" className="gap-2 px-8" asChild>
              <Link to="/dashboard">
                Subscribe
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
            <SignedIn>
              <Button variant="outline" size="lg" className="px-8" asChild>
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            </SignedIn>
            <SignedOut>
              <Button variant="outline" size="lg" className="px-8" asChild>
                <Link to="/sign-in">Sign In</Link>
              </Button>
            </SignedOut>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">Cancel any time</p>
        </FadeIn>
      </div>

      {/* Faux Discord embed preview */}
      <FadeIn
        immediate
        delay={260}
        className="relative z-10 mx-auto mt-20 w-full max-w-2xl mb-20"
        translateY={40}
      >
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-black/40">
          {/* Title bar */}
          <div className="flex items-center gap-2 border-b border-border bg-secondary/50 px-4 py-3">
            <div className="h-3 w-3 rounded-full bg-red-500/70" />
            <div className="h-3 w-3 rounded-full bg-yellow-500/70" />
            <div className="h-3 w-3 rounded-full bg-green-500/70" />
            <span className="ml-2 text-xs text-muted-foreground">
              # server-management
            </span>
          </div>
          {/* Fake message */}
          <div className="space-y-3 p-5 text-sm">
            <div className="flex items-start gap-3">
              <img
                src="/icon-256.png"
                alt="Overseer"
                className="h-8 w-8 shrink-0 rounded-full"
              />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-purple-400">
                    Overseer
                  </span>
                  <span className="rounded bg-primary/20 px-1 text-[10px] font-medium text-primary">
                    BOT
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Today at 14:32
                  </span>
                </div>
                {/* Embed */}
                <div className="mt-2 rounded-r-lg border-l-4 border-primary bg-secondary/60 p-3">
                  <p className="mb-1 font-semibold text-foreground">
                    🔨 Player Banned
                  </p>
                  <div className="space-y-0.5 text-xs text-muted-foreground">
                    <p>
                      <span className="text-foreground/70">Player:</span>{" "}
                      ShadyGamer99
                    </p>
                    <p>
                      <span className="text-foreground/70">Reason:</span>{" "}
                      Spoofing platform identity
                    </p>
                    <p>
                      <span className="text-foreground/70">Servers:</span> All
                      (5/5)
                    </p>
                    <p>
                      <span className="text-foreground/70">Banned by:</span>{" "}
                      AdminUser
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="relative py-28 px-6">
      <div className="relative mx-auto max-w-7xl">
        <FadeIn>
          <div className="mb-16 text-center">
            <Badge variant="secondary" className="mb-4">
              Features
            </Badge>
            <h2 className="text-4xl font-bold tracking-tight text-foreground">
              Everything your admins need
            </h2>
            <p className="mt-4 text-muted-foreground">
              From basic moderation to advanced anti-cheat — Overseer has you
              covered.
            </p>
          </div>
        </FadeIn>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Basic */}
          <FadeIn delay={100}>
            <div className="rounded-2xl border border-border bg-card/30 p-8 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150">
              <div className="mb-6">
                <h3 className="font-semibold text-foreground">Basic Tier</h3>
                <p className="text-xs text-muted-foreground">
                  Core admin tools
                </p>
              </div>
              <ul className="space-y-3">
                {basicFeatures.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm">
                    <Icon className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-muted-foreground">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          {/* Advanced */}
          <FadeIn delay={250}>
            <div className="rounded-2xl border border-primary/30 bg-card/30 p-8 shadow-xl shadow-primary/10 backdrop-blur-md backdrop-saturate-150">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-foreground">
                    Advanced Tier
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Anti-cheat & deep analytics
                  </p>
                </div>
                <Badge>Recommended</Badge>
              </div>
              <ul className="space-y-3">
                {advancedFeatures.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm">
                    <Icon className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-muted-foreground">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { value: "5,000+", label: "Servers managed" },
    { value: "50ms", label: "Avg. response time" },
    { value: "99.9%", label: "Uptime SLA" },
    { value: "24/7", label: "Log monitoring" },
  ];

  return (
    <section className="relative py-16 px-6">
      <div className="relative mx-auto grid max-w-5xl grid-cols-2 gap-8 md:grid-cols-4">
        {stats.map(({ value, label }, i) => (
          <FadeIn key={label} delay={i * 100} translateY={20}>
            <div className="text-center">
              <p className="text-4xl font-extrabold text-foreground">{value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{label}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="relative py-28 px-6">
      <div className="relative mx-auto max-w-5xl">
        <FadeIn>
          <div className="mb-16 text-center">
            <Badge variant="secondary" className="mb-4">
              Pricing
            </Badge>
            <h2 className="text-4xl font-bold tracking-tight text-foreground">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-muted-foreground">
              One bot. One cluster. Billed monthly. Cancel any time.
            </p>
          </div>
        </FadeIn>

        <div className="grid gap-6 md:grid-cols-2">
          {plans.map((plan, i) => (
            <FadeIn key={plan.name} delay={i * 150}>
              <Card
                className={
                  plan.badge
                    ? "relative border-primary/50 bg-card/30 shadow-xl shadow-primary/10 backdrop-blur-md backdrop-saturate-150"
                    : "relative bg-card/30 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150"
                }
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge>{plan.badge}</Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="flex items-baseline gap-1 pt-2">
                    <span className="text-4xl font-extrabold text-foreground">
                      {plan.price}
                    </span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-2.5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm">
                        <Check className="h-4 w-4 shrink-0 text-primary" />
                        <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={plan.ctaVariant}
                    size="lg"
                    className="w-full"
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="px-6 pb-28">
      <FadeIn translateY={32}>
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-primary/30 bg-card p-12 text-center shadow-2xl shadow-primary/10">
          {/* Glow */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-64 w-64 rounded-full bg-primary/15 blur-[80px]" />
          </div>

          <div className="relative z-10">
            <Badge variant="secondary" className="mb-6">
              Ready to take control?
            </Badge>
            <h2 className="mb-4 text-4xl font-extrabold tracking-tight text-foreground">
              Start managing smarter
            </h2>
            <p className="mx-auto mb-8 max-w-lg text-muted-foreground">
              Add Overseer to your Discord today and get full control of your
              ARK cluster in minutes.
            </p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button size="lg" className="gap-2 px-8" asChild>
              <Link to="/dashboard">
                Subscribe now
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
            <SignedIn>
              <Button variant="outline" size="lg" className="px-8" asChild>
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            </SignedIn>
            <SignedOut>
              <Button variant="outline" size="lg" className="px-8" asChild>
                <Link to="/sign-in">Sign In</Link>
              </Button>
            </SignedOut>
            </div>
          </div>
        </div>
      </FadeIn>
    </section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

function LandingPage() {
  return (
    <div className="relative min-h-screen">
      <BackgroundGlow />
      <div className="relative">
        <Hero />
        <Stats />
        <Features />
        <Pricing />
        <CTA />
      </div>
    </div>
  );
}
